import React, { useEffect, useState } from "react";
import { influencerCampaingnsRequest, postCampaignShare, fetchCampaignSharesByUser } from "../../services/Common.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { themes } from '../../theme.ts';
import axios from "axios";
import toast from "react-hot-toast";


interface CampaignRequest {
  id: number;
  campaign: {
    id: number;
    title: string;
    description?: string;
    banner?: string;
    organization?: {
      name: string;
    };
  };
  status: string;
  created_at: string;
  updated_at: string;
}

interface CampaignShare {
  id: string;
  campaign: {
    id: string;
    brand_details: {
      title: string;
      logo?: string;
    };
    title: string;
  };
  campaign_title: string;
  task_completed: boolean;
  post_link: {
    [platform: string]: Array<{
      link: string;
      status: string;
    }>;
  };
  posted_at: string;
  is_verified: boolean;
  verified_at: string | null;
}

const InfluencerRequests: React.FC = () => {
  const { themeName } = useTheme();
  const [requests, setRequests] = useState<CampaignRequest[]>([]);
  const [campaignShares, setCampaignShares] = useState<CampaignShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<CampaignRequest | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<CampaignRequest | null>(null);
  const [editData, setEditData] = useState({
    link: "",
    platform: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'requests' | 'shares'>('requests');

  // Get user data from localStorage
  const userData = localStorage.getItem('user');
  const user = userData ? JSON.parse(userData) : null;
  const token = user?.access;
  const userId = user?.user?.id;

  // Fetch user campaign requests from API
  useEffect(() => {
    const fetchRequests = async () => {
      if (!token || !userId) {
        setError("Authentication token or user ID not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await influencerCampaingnsRequest(token, userId) as any;

        console.log('API Response:', response);

        if ((response as any).status === 200 && (response as any).data) {
          setRequests(response.data);
        } else {
          setError("Failed to fetch campaign requests");
          toast.error("Failed to fetch campaign requests");
        }
      } catch (err) {
        console.error("Error fetching campaign requests:", err);
        const errorMessage = (err as any).response?.data?.message || "An error occurred while fetching requests";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [token, userId]);

  // Fetch campaign shares when tab is switched or component mounts
  useEffect(() => {
    if (activeTab === 'shares' && token && userId) {
      fetchCampaignShares();
    }
  }, [activeTab, token, userId]);

  const fetchCampaignShares = async () => {
    if (!token || !userId) return;

    try {
      setSharesLoading(true);
      const response = await fetchCampaignSharesByUser(token, userId);

      if (response.status === 200 && response.data) {
        setCampaignShares(response.data);
      } else {
        toast.error("Failed to fetch campaign shares");
      }
    } catch (err: any) {
      console.error("Error fetching campaign shares:", err);
      const errorMessage = err.response?.data?.message || "An error occurred while fetching shares";
      toast.error(errorMessage);
    } finally {
      setSharesLoading(false);
    }
  };

  const handleEdit = (request: CampaignRequest) => {
    if (request.status.toLowerCase() === 'approved') {
      setSelectedRequest(request);
      setEditData({
        link: "",
        platform: ""
      });
      setShowEditPopup(true);
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedRequest || !editData.link || !editData.platform) {
      toast.error("Please fill in both link and platform");
      return;
    }

    setSubmitting(true);
    try {
      // FIXED: Change from array of objects to just the link string
      const shareData = {
        campaign: selectedRequest.campaign.id,
        user: parseInt(userId),
        task_completed: false,
        post_link: {
          [editData.platform]: editData.link  // Changed from array to string
        }
      };

      console.log('Submitting campaign share:', shareData);

      const response = await postCampaignShare(token, shareData);

      if (response.status === 201 || response.status === 200) {
        toast.success(`Successfully submitted link for "${selectedRequest.campaign.title}"`);

        // Refresh campaign shares
        if (activeTab === 'shares') {
          fetchCampaignShares();
        }

        setShowEditPopup(false);
        setSelectedRequest(null);
        setEditData({ link: "", platform: "" });
      } else {
        throw new Error(response.data?.message || "Failed to submit link");
      }

    } catch (error: any) {
      console.error("Error submitting campaign share:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to submit link";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (request: CampaignRequest) => {
    setRequestToDelete(request);
    setShowDeleteDialog(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setRequestToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;

    try {
      const deletePromise = axios.delete(
        `${process.env.REACT_APP_DB_URI}/campaign-request/${requestToDelete.id}/`,
        {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        }
      );

      // Show loading toast and wait for API call
      await toast.promise(
        deletePromise,
        {
          loading: 'Deleting request...',
          success: 'Request deleted successfully',
          error: 'Failed to delete request'
        }
      );

      // Remove from local state only if API call succeeds
      setRequests((prev) => prev.filter((req) => req.id !== requestToDelete.id));
      setShowDeleteDialog(false);
      setRequestToDelete(null);

    } catch (error: any) {
      console.error("Error deleting request:", error);
      const errorMessage = error.response?.data?.message || "Failed to delete request";
      toast.error(errorMessage);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return '✅';
      case 'rejected':
        return '❌';
      case 'pending':
        return '⏳';
      case 'completed':
        return '✅';
      default:
        return '📋';
    }
  };

  const getVerificationIcon = (isVerified: boolean) => {
    return isVerified ? '✅' : '⏳';
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return '📷';
      case 'youtube':
        return '🎥';
      case 'tiktok':
        return '🎵';
      case 'twitter':
      case 'x':
        return '🐦';
      case 'facebook':
        return '👥';
      case 'linkedin':
        return '💼';
      default:
        return '🔗';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const canEdit = (status: string) => {
    return status.toLowerCase() === 'approved';
  };

  const canDelete = (status: string) => {
    return status.toLowerCase() === 'pending';
  };

  if (loading && activeTab === 'requests') {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: themes[themeName].colors.background }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4"
            style={{ borderColor: themes[themeName].colors.primary }}
          ></div>
          <p
            className="text-lg"
            style={{ color: themes[themeName].colors.textSecondary }}
          >
            Loading your campaign requests...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: themes[themeName].colors.background }}
      >
        <div
          className="text-center p-8 rounded-xl shadow-lg max-w-md"
          style={{
            backgroundColor: themes[themeName].colors.surface,
            borderColor: themes[themeName].colors.border,
            borderWidth: 1,
            borderStyle: 'solid'
          }}
        >
          <div className="text-6xl mb-4" style={{ color: themes[themeName].colors.error }}>⚠️</div>
          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: themes[themeName].colors.error }}
          >
            Error Loading Requests
          </h3>
          <p
            className="mb-4"
            style={{ color: themes[themeName].colors.textSecondary }}
          >
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: themes[themeName].colors.primary,
              color: themes[themeName].colors.surface
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-6"
      style={{ backgroundColor: themes[themeName].colors.background }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className="text-4xl font-bold mb-3"
            style={{ color: themes[themeName].colors.text }}
          >
            My Campaign Activities
          </h1>
          <p
            className="text-lg"
            style={{ color: themes[themeName].colors.textSecondary }}
          >
            Track your campaign applications and submissions
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b mb-8" style={{ borderColor: themes[themeName].colors.border }}>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${activeTab === 'requests' ? '' : 'opacity-70'
              }`}
            style={{
              borderColor: activeTab === 'requests' ? themes[themeName].colors.primary : 'transparent',
              color: activeTab === 'requests' ? themes[themeName].colors.primary : themes[themeName].colors.textSecondary
            }}
          >
            Campaign Requests ({requests.length})
          </button>
          <button
            onClick={() => setActiveTab('shares')}
            className={`px-6 py-3 font-semibold border-b-2 transition-colors ${activeTab === 'shares' ? '' : 'opacity-70'
              }`}
            style={{
              borderColor: activeTab === 'shares' ? themes[themeName].colors.primary : 'transparent',
              color: activeTab === 'shares' ? themes[themeName].colors.primary : themes[themeName].colors.textSecondary
            }}
          >
            Campaign Submissions ({campaignShares.length})
          </button>
        </div>

        {/* Stats Overview */}
        {activeTab === 'requests' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div
              className="rounded-xl shadow p-4 text-center"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderLeftColor: themes[themeName].colors.primary,
                borderLeftWidth: 4,
                borderLeftStyle: 'solid'
              }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: themes[themeName].colors.primary }}
              >
                {requests.length}
              </div>
              <div
                className="text-sm"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                Total Requests
              </div>
            </div>
            <div
              className="rounded-xl shadow p-4 text-center"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderLeftColor: themes[themeName].colors.warning,
                borderLeftWidth: 4,
                borderLeftStyle: 'solid'
              }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: themes[themeName].colors.warning }}
              >
                {requests.filter(req => req.status.toLowerCase() === 'pending').length}
              </div>
              <div
                className="text-sm"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                Pending
              </div>
            </div>
            <div
              className="rounded-xl shadow p-4 text-center"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderLeftColor: themes[themeName].colors.success,
                borderLeftWidth: 4,
                borderLeftStyle: 'solid'
              }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: themes[themeName].colors.success }}
              >
                {requests.filter(req => req.status.toLowerCase() === 'approved').length}
              </div>
              <div
                className="text-sm"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                Approved
              </div>
            </div>
            <div
              className="rounded-xl shadow p-4 text-center"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderLeftColor: themes[themeName].colors.error,
                borderLeftWidth: 4,
                borderLeftStyle: 'solid'
              }}
            >
              <div
                className="text-2xl font-bold"
                style={{ color: themes[themeName].colors.error }}
              >
                {requests.filter(req => req.status.toLowerCase() === 'rejected').length}
              </div>
              <div
                className="text-sm"
                style={{ color: themes[themeName].colors.textSecondary }}
              >
                Rejected
              </div>
            </div>
          </div>
        )}

        {/* Campaign Requests Table */}
        {activeTab === 'requests' && (
          <div
            className="rounded-xl shadow-lg overflow-hidden"
            style={{
              backgroundColor: themes[themeName].colors.surface,
              borderColor: themes[themeName].colors.border,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y"
                style={{ borderColor: themes[themeName].colors.border }}
              >
                <thead style={{ backgroundColor: themes[themeName].colors.primary }}>
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Campaign Name
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Status
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Applied Date
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Last Updated
                    </th>
                    <th
                      className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: themes[themeName].colors.surface }}>
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-6xl mb-4">📋</div>
                          <h3
                            className="text-xl font-semibold mb-2"
                            style={{ color: themes[themeName].colors.text }}
                          >
                            No Campaign Requests
                          </h3>
                          <p
                            className="mb-4"
                            style={{ color: themes[themeName].colors.textSecondary }}
                          >
                            You haven't applied to any campaigns yet.
                          </p>
                          <button
                            onClick={() => window.location.href = '/dashboard/campaings'}
                            className="px-6 py-2 rounded-lg transition-colors"
                            style={{
                              backgroundColor: themes[themeName].colors.primary,
                              color: themes[themeName].colors.surface
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            Browse Campaigns
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    requests.map((req) => (
                      <tr
                        key={req.id}
                        style={{ cursor: 'default' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themes[themeName].colors.card)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themes[themeName].colors.surface)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div
                                className="text-lg font-bold"
                                style={{ color: themes[themeName].colors.text }}
                              >
                                {req.campaign?.title || "Unknown Campaign"}
                              </div>
                              {req.campaign?.description && (
                                <div
                                  className="text-xs mt-1 line-clamp-2"
                                  style={{ color: themes[themeName].colors.textMuted }}
                                >
                                  {req.campaign.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor:
                                req.status.toLowerCase() === 'approved'
                                  ? themes[themeName].colors.success + '33'
                                  : req.status.toLowerCase() === 'rejected'
                                    ? themes[themeName].colors.error + '33'
                                    : req.status.toLowerCase() === 'pending'
                                      ? themes[themeName].colors.warning + '33'
                                      : req.status.toLowerCase() === 'completed'
                                        ? themes[themeName].colors.primary + '33'
                                        : themes[themeName].colors.border,
                              color:
                                req.status.toLowerCase() === 'approved'
                                  ? themes[themeName].colors.success
                                  : req.status.toLowerCase() === 'rejected'
                                    ? themes[themeName].colors.error
                                    : req.status.toLowerCase() === 'pending'
                                      ? themes[themeName].colors.warning
                                      : req.status.toLowerCase() === 'completed'
                                        ? themes[themeName].colors.primary
                                        : themes[themeName].colors.text,
                            }}
                          >
                            {getStatusIcon(req.status)} {req.status}
                          </span>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          style={{ color: themes[themeName].colors.textSecondary }}
                        >
                          {formatDate(req.created_at)}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          style={{ color: themes[themeName].colors.textSecondary }}
                        >
                          {formatDate(req.updated_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-3">
                            {canEdit(req.status) && (
                              <button
                                onClick={() => handleEdit(req)}
                                className="transition-colors flex items-center"
                                style={{ color: themes[themeName].colors.primary }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                title="Submit Post Link"
                              >
                                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                                Submit Link
                              </button>
                            )}
                            {canDelete(req.status) && (
                              <button
                                onClick={() => handleDeleteClick(req)}
                                className="transition-colors flex items-center"
                                style={{ color: themes[themeName].colors.error }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                title="Delete Request"
                              >
                                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Withdraw
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Campaign Shares Table */}
        {activeTab === 'shares' && (
          <div
            className="rounded-xl shadow-lg overflow-hidden"
            style={{
              backgroundColor: themes[themeName].colors.surface,
              borderColor: themes[themeName].colors.border,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y"
                style={{ borderColor: themes[themeName].colors.border }}
              >
                <thead style={{ backgroundColor: themes[themeName].colors.primary }}>
                  <tr>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Campaign
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Platform & Links
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Submitted Date
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider"
                      style={{ color: themes[themeName].colors.surface }}
                    >
                      Verification Status
                    </th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: themes[themeName].colors.surface }}>
                  {sharesLoading ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div
                            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
                            style={{ borderColor: themes[themeName].colors.primary }}
                          ></div>
                          <p style={{ color: themes[themeName].colors.textSecondary }}>
                            Loading campaign submissions...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : campaignShares.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-6xl mb-4">📤</div>
                          <h3
                            className="text-xl font-semibold mb-2"
                            style={{ color: themes[themeName].colors.text }}
                          >
                            No Campaign Submissions
                          </h3>
                          <p
                            className="mb-4"
                            style={{ color: themes[themeName].colors.textSecondary }}
                          >
                            You haven't submitted any campaign posts yet.
                          </p>
                          <button
                            onClick={() => setActiveTab('requests')}
                            className="px-6 py-2 rounded-lg transition-colors"
                            style={{
                              backgroundColor: themes[themeName].colors.primary,
                              color: themes[themeName].colors.surface
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                          >
                            View Campaign Requests
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    campaignShares.map((share) => (
                      <tr
                        key={share.id}
                        style={{ cursor: 'default' }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = themes[themeName].colors.card)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themes[themeName].colors.surface)}
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div
                              className="text-lg font-bold"
                              style={{ color: themes[themeName].colors.text }}
                            >
                              {share.campaign_title}
                            </div>
                            <div
                              className="text-sm"
                              style={{ color: themes[themeName].colors.textSecondary }}
                            >
                              {share.campaign.brand_details.title}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            {Object.entries(share.post_link).map(([platform, links]) => (
                              <div key={platform} className="flex items-center gap-2">
                                <span className="text-lg">{getPlatformIcon(platform)}</span>
                                <div>
                                  <div className="font-medium capitalize" style={{ color: themes[themeName].colors.text }}>
                                    {platform}
                                  </div>
                                  {links.map((link, index) => (
                                    <div key={index} className="text-sm">
                                      <a
                                        href={link.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline"
                                        style={{ color: themes[themeName].colors.primary }}
                                      >
                                        {link.link}
                                      </a>
                                      <span
                                        className="text-xs ml-2 capitalize"
                                        style={{ color: themes[themeName].colors.textMuted }}
                                      >
                                        ({link.status})
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm"
                          style={{ color: themes[themeName].colors.textSecondary }}
                        >
                          {formatDate(share.posted_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: share.is_verified
                                ? themes[themeName].colors.success + '33'
                                : themes[themeName].colors.warning + '33',
                              color: share.is_verified
                                ? themes[themeName].colors.success
                                : themes[themeName].colors.warning,
                            }}
                          >
                            {getVerificationIcon(share.is_verified)}
                            {share.is_verified ? 'Verified' : 'Pending Verification'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Popup - For submitting post link */}
        {showEditPopup && selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-xl shadow-lg max-w-md w-full p-6"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderColor: themes[themeName].colors.border,
                borderWidth: 1,
                borderStyle: 'solid'
              }}
            >
              <h3
                className="text-xl font-semibold mb-4"
                style={{ color: themes[themeName].colors.text }}
              >
                Submit Post Link
              </h3>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Campaign: {selectedRequest.campaign.title}
                </label>
                <p
                  className="text-sm"
                  style={{ color: themes[themeName].colors.textSecondary }}
                >
                  Status: <span style={{ color: themes[themeName].colors.success }}>Approved - Ready for posting</span>
                </p>
              </div>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Post URL *
                </label>
                <input
                  type="url"
                  value={editData.link}
                  onChange={(e) => setEditData({ ...editData, link: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border transition-colors"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    color: themes[themeName].colors.text
                  }}
                  placeholder="https://instagram.com/p/your-post"
                  required
                />
              </div>

              <div className="mb-6">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Platform *
                </label>
                <select
                  value={editData.platform}
                  onChange={(e) => setEditData({ ...editData, platform: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border transition-colors"
                  style={{
                    backgroundColor: themes[themeName].colors.background,
                    borderColor: themes[themeName].colors.border,
                    color: themes[themeName].colors.text
                  }}
                  required
                >
                  <option value="">Select Platform</option>
                  <option value="instagram">Instagram</option>
                  <option value="youtube">YouTube</option>
                  <option value="tiktok">TikTok</option>
                  <option value="twitter">Twitter</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditPopup(false);
                    setSelectedRequest(null);
                    setEditData({ link: "", platform: "" });
                  }}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: themes[themeName].colors.border,
                    color: themes[themeName].colors.text
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                  style={{
                    backgroundColor: submitting ? themes[themeName].colors.textMuted : themes[themeName].colors.primary,
                    color: themes[themeName].colors.surface
                  }}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                      Submitting...
                    </>
                  ) : (
                    'Submit Link'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && requestToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div
              className="rounded-xl shadow-lg max-w-md w-full p-6"
              style={{
                backgroundColor: themes[themeName].colors.surface,
                borderColor: themes[themeName].colors.border,
                borderWidth: 1,
                borderStyle: 'solid'
              }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: themes[themeName].colors.error + '20' }}>
                  <svg
                    className="w-8 h-8"
                    style={{ color: themes[themeName].colors.error }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: themes[themeName].colors.text }}
                >
                  Delete Campaign Request
                </h3>
                <p
                  className="text-sm mb-4"
                  style={{ color: themes[themeName].colors.textSecondary }}
                >
                  Are you sure you want to delete your request for
                </p>
                <p
                  className="font-semibold text-lg mb-2"
                  style={{ color: themes[themeName].colors.primary }}
                >
                  "{requestToDelete.campaign.title}"?
                </p>
                <p
                  className="text-xs italic"
                  style={{ color: themes[themeName].colors.textMuted }}
                >
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-6 py-2 rounded-lg transition-colors font-medium"
                  style={{
                    backgroundColor: themes[themeName].colors.border,
                    color: themes[themeName].colors.text
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-6 py-2 rounded-lg transition-colors font-medium"
                  style={{
                    backgroundColor: themes[themeName].colors.error,
                    color: themes[themeName].colors.surface
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Delete Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Additional Info */}
        {requests.length > 0 && activeTab === 'requests' && (
          <div
            className="mt-6 rounded-xl p-6"
            style={{
              backgroundColor: themes[themeName].colors.card,
              borderColor: themes[themeName].colors.border,
              borderWidth: 1,
              borderStyle: 'solid'
            }}
          >
            <h3
              className="text-lg font-semibold mb-3"
              style={{ color: themes[themeName].colors.text }}
            >
              📊 Request Status Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center">
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: themes[themeName].colors.warning }}
                ></span>
                <span style={{ color: themes[themeName].colors.textSecondary }}>
                  Pending: Under review (Can Delete only)
                </span>
              </div>
              <div className="flex items-center">
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: themes[themeName].colors.success }}
                ></span>
                <span style={{ color: themes[themeName].colors.textSecondary }}>
                  Approved: Ready to submit post link
                </span>
              </div>
              <div className="flex items-center">
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: themes[themeName].colors.error }}
                ></span>
                <span style={{ color: themes[themeName].colors.textSecondary }}>
                  Rejected: Request declined (No actions)
                </span>
              </div>
              {/* <div className="flex items-center">
                <span
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: themes[themeName].colors.primary }}
                ></span>
                <span style={{ color: themes[themeName].colors.textSecondary }}>
                  Completed: Post link submitted
                </span>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfluencerRequests;